import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Sparkles, Loader2, MapPin, Building2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";

function fmtInterviewDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.round((d - new Date()) / 86400000);
  const dateStr = d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", weekday: "short" });
  const timeStr = d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  let rel = "";
  if (days === 0) rel = "hoje";
  else if (days === 1) rel = "amanhã";
  else if (days > 1 && days < 7) rel = `daqui a ${days} dias`;
  else if (days >= 7) rel = `daqui a ${Math.ceil(days / 7)} semana(s)`;
  return { dateStr, timeStr, rel };
}

export function UpcomingInterviews({ onOpenPrep, limit = 5 }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/candidate/interviews/upcoming");
      setItems((data.interviews || []).slice(0, limit));
    } catch (e) {
      toast.error(formatErr(e));
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      data-testid="upcoming-interviews"
      className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-sky-500 text-white flex items-center justify-center">
            <CalendarClock className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-heading font-black tracking-tighter text-lg text-slate-900">Próximas entrevistas</h2>
            <p className="text-xs text-slate-500">Prepara-te com o teu coach IA</p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      )}

      {items && items.length === 0 && (
        <div className="py-6 text-center text-sm text-slate-500">
          Sem entrevistas agendadas. Quando uma empresa marcar uma entrevista, ela aparece aqui.
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((i) => {
            const fmt = fmtInterviewDate(i.interview_scheduled_at);
            return (
              <li key={i.id}
                data-testid={`interview-${i.id}`}
                className="rounded-xl border border-black/5 p-4 hover:border-blue-200 hover:shadow-sm transition">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-700 flex flex-col items-center justify-center shrink-0">
                    <span className="text-[10px] uppercase font-bold leading-none">{fmt.dateStr?.split(" ")[1]}</span>
                    <span className="font-heading font-black text-lg leading-none">{fmt.dateStr?.split(" ")[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/empregos/${i.job_slug}`}
                      className="font-heading font-bold tracking-tighter text-slate-900 hover:text-amber-600 transition line-clamp-1">
                      {i.job_title}
                    </Link>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600 mt-0.5">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{i.company_name}</span>
                      <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />{fmt.timeStr} · {fmt.rel}</span>
                    </div>
                    {i.interview_notes && (
                      <p className="text-xs text-slate-500 mt-1.5 line-clamp-2"><MapPin className="w-3 h-3 inline mr-1" />{i.interview_notes}</p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <Button
                        data-testid={`interview-prep-btn-${i.id}`}
                        size="sm" variant="outline"
                        className="rounded-full text-xs h-8"
                        onClick={() => onOpenPrep && onOpenPrep(i)}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" />
                        {i.has_prep ? "Ver preparação IA" : "Preparar com IA"}
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
